import {
  Alert,
  AlertDescription,
  Field,
  FieldHelp,
  FieldLabel,
  Input
} from '@axilon-apps/libs-ui-shadcn'
import { useMemo } from 'react'
import { z as zod } from 'zod'
import { ShieldCheck, AppWindow } from 'lucide-react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import { PasswordInput, FlashMessages, LoadingButton } from '@axilon-apps/libs-ui-components'
import type { Maybe } from '@axilon-apps/libs-shared-types'

import { FlashMessageGroup } from '../../../../constants/flash-message-groups'

const baseSchema = zod.object({
  organizationSlug: zod.string(),
  username: zod.string().min(1, { message: 'Username is required' }),
  password: zod.string().min(6, { message: 'Password is required' }),
  otp: zod
    .string()
    .regex(/^\d{6}$/, { message: 'MFA Code must be exactly 6 digits' })
    .optional()
    .or(zod.literal(''))
})

const schema = (mfaRequired: boolean, organizationRequired: boolean) =>
  baseSchema.extend({
    organizationSlug: organizationRequired
      ? zod.string().min(1, { message: 'Organization is required' })
      : zod.string(),
    otp: mfaRequired
      ? zod.string().regex(/^\d{6}$/, { message: 'MFA Code must be exactly 6 digits' })
      : zod.string().optional().or(zod.literal(''))
  })

export type FormData = zod.infer<typeof baseSchema>

interface Props {
  onSubmit: (data: FormData) => void
  onWindowsSignOn: () => void
  isLoading: boolean
  mfaStep: boolean
  fieldErrors?: Partial<Record<keyof FormData, Maybe<string[]>>>
  reset: () => void
  windowsSSOLoading: boolean
  showWindowsLogin?: boolean
  showOrganizationSlug?: boolean
}

const LoginForm = ({
  isLoading,
  fieldErrors,
  mfaStep = false,
  onSubmit,
  reset: resetMfa,
  onWindowsSignOn,
  windowsSSOLoading,
  showWindowsLogin = false,
  showOrganizationSlug = false
}: Props) => {
  const defaultValues = {
    organizationSlug: '',
    username: '',
    password: '',
    otp: ''
  } satisfies FormData

  const resolver = useMemo(
    () => zodResolver(schema(mfaStep, showOrganizationSlug)),
    [mfaStep, showOrganizationSlug]
  )

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset: resetForm,
    watch
  } = useForm<FormData>({
    defaultValues,
    mode: 'onBlur',
    resolver
  })

  const reset = () => {
    resetMfa()
    resetForm()
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-4">
        {showOrganizationSlug && (
          <Controller
            control={control}
            name="organizationSlug"
            render={({ field }) => (
              <Field
                data-invalid={
                  Boolean(errors.organizationSlug || fieldErrors?.organizationSlug) || undefined
                }
              >
                <FieldLabel>Organization</FieldLabel>
                <Input {...field} type="text" disabled={mfaStep} />
                <FieldHelp className="text-destructive">
                  {errors.organizationSlug?.message || fieldErrors?.organizationSlug?.join(', ')}
                </FieldHelp>
              </Field>
            )}
          />
        )}
        <Controller
          control={control}
          name="username"
          render={({ field }) => (
            <Field data-invalid={Boolean(errors.username || fieldErrors?.username) || undefined}>
              <FieldLabel>Username</FieldLabel>
              <Input {...field} type="text" disabled={mfaStep} />
              <FieldHelp className="text-destructive">
                {errors.username?.message || fieldErrors?.username?.join(', ')}
              </FieldHelp>
            </Field>
          )}
        />
        <PasswordInput
          control={control}
          name="password"
          error={errors.password?.message || fieldErrors?.password?.join(', ')}
          label="Password"
          showComplexity={false}
          disabled={mfaStep}
          watch={watch}
        />
        {mfaStep && (
          <Controller
            control={control}
            name="otp"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.otp || fieldErrors?.otp) || undefined}>
                <FieldLabel required>MFA Code</FieldLabel>
                <Input {...field} type="number" />
                <FieldHelp className="text-destructive">
                  {errors.otp?.message || fieldErrors?.otp?.join(', ')}
                </FieldHelp>
              </Field>
            )}
          />
        )}
        {mfaStep && (
          <Alert>
            <ShieldCheck className="size-4" strokeWidth={2.5} />
            <AlertDescription>Please provide a code from your Authenticator</AlertDescription>
          </Alert>
        )}
        <FlashMessages group={FlashMessageGroup.LoginMessage} />
        <LoadingButton loading={isLoading} type="submit">
          Sign In
        </LoadingButton>
        {mfaStep && (
          <LoadingButton loading={isLoading} type="reset" onClick={reset}>
            Reset
          </LoadingButton>
        )}
        {showWindowsLogin && (
          <LoadingButton
            loading={windowsSSOLoading}
            onClick={onWindowsSignOn}
            type="button"
            startDecorator={<AppWindow className="size-4" strokeWidth={2.5} />}
          >
            Authenticate with Windows
          </LoadingButton>
        )}
      </div>
    </form>
  )
}

export default LoginForm
